import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { brandColorPattern, contrastLabel } from '@brimo/shared';
import { Skeleton } from '../../../../ds/components';
import { api, errorMessage } from '../../../../lib/api';
import { WizardFooter, WizardIntro } from '../../ui';
import { organizationKeys, useBrand } from '../api';
import { ColorSwatches, LogoSlots, MailPreview, PortalPreview, brandStyles } from '../BrandParts';
import { LoadError } from '../LoadError';
import type { Brand } from '../types';
import { useWizardNav } from '../wizardContext';
import { stepEyebrow } from '../wizardSteps';
import styles from './steps.module.css';

/** Saves the accent colour (`PATCH /settings/organization/brand`) and keeps the cache in step. */
export function useSaveColor(onSaved?: () => void) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  async function save(color: string) {
    setError(null);
    const previous = queryClient.getQueryData<Brand>(organizationKeys.brand);
    if (previous) queryClient.setQueryData(organizationKeys.brand, { ...previous, color });
    try {
      const next = await api<Brand>('/settings/organization/brand', { method: 'PATCH', body: { color } });
      queryClient.setQueryData(organizationKeys.brand, next);
      onSaved?.();
    } catch (failure) {
      if (previous) queryClient.setQueryData(organizationKeys.brand, previous);
      setError(errorMessage(failure));
    }
  }
  return { save, error };
}

/** Onboarding Admin 03 · Marke: logo slots, accent colour with contrast line, live previews. */
export function BrandStep() {
  const nav = useWizardNav();
  const brand = useBrand();
  const color = useSaveColor();
  const [pending, setPending] = useState<'skip' | 'continue' | null>(null);
  const [stepError, setStepError] = useState<string | null>(null);

  async function leave(action: 'skip' | 'continue') {
    setPending(action);
    setStepError(null);
    try {
      await nav.complete('brand', action, 'company');
    } catch (error) {
      setStepError(errorMessage(error));
    } finally {
      setPending(null);
    }
  }

  const current = brand.data?.color ?? null;
  const contrast =
    current && brandColorPattern.test(current) ? contrastLabel(current) : brand.data?.contrastLabel;

  return (
    <>
      <WizardIntro
        eyebrow={stepEyebrow('brand')}
        title="Ihre Marke"
        text="Logo und Farbe erscheinen im Mandantenportal, in Exposés, Terminbestätigungen und jeder ausgehenden Mail."
      />
      {brand.isPending ? (
        <div className={styles.brandGrid}>
          <Skeleton variant="block" height={460} />
          <Skeleton variant="block" height={420} />
        </div>
      ) : brand.isError ? (
        <LoadError
          what="Die Marke konnte nicht geladen werden"
          error={brand.error}
          retrying={brand.isFetching}
          onRetry={() => void brand.refetch()}
        />
      ) : (
        <div className={styles.brandGrid}>
          <div className={styles.column}>
            <section className={styles.card} aria-labelledby="brand-logo-title">
              <h2 id="brand-logo-title" className={styles.cardTitle}>
                Logo hochladen
              </h2>
              <LogoSlots brand={brand.data} variant="wizard" />
              <span className={brandStyles.rule}>
                SVG bevorzugt, sonst PNG mit transparentem Hintergrund ab 600 px Breite. Maximal 2 MB.
              </span>
            </section>
            <section className={styles.card} aria-labelledby="brand-color-title">
              <div className={styles.cardTitleRow}>
                <h2 id="brand-color-title" className={styles.cardTitle}>
                  Akzentfarbe
                </h2>
                <span className={styles.cardAside}>für Schaltflächen und Hervorhebungen</span>
              </div>
              <ColorSwatches
                presets={brand.data.colorPresets}
                value={current}
                withHex
                onPick={(value) => void color.save(value)}
              />
              {contrast ? <div className={brandStyles.contrast}>{contrast}</div> : null}
              {color.error ? (
                <span className={styles.error} role="alert">
                  {color.error}
                </span>
              ) : null}
            </section>
          </div>
          <div className={styles.columnTight}>
            <section className={brandStyles.previewCard} aria-label="Vorschau Mandantenportal">
              <span className={brandStyles.previewTitle}>Vorschau Mandantenportal</span>
              <PortalPreview brand={brand.data} color={current} variant="wizard" />
            </section>
            <section className={brandStyles.previewCard} aria-label="Vorschau Mailkopf">
              <span className={brandStyles.previewTitle}>Vorschau Mailkopf</span>
              <MailPreview brand={brand.data} />
            </section>
          </div>
        </div>
      )}
      {stepError ? (
        <div className={styles.error} role="alert">
          {stepError}
        </div>
      ) : null}
      <WizardFooter
        note="Alles später in den Einstellungen änderbar."
        secondary={{ label: 'Überspringen', loading: pending === 'skip', onClick: () => void leave('skip') }}
        primary={{ label: 'Weiter', loading: pending === 'continue', onClick: () => void leave('continue') }}
      />
    </>
  );
}
